import type { CustomContributions,
    DefaultContributions,
    InputRates,
    ModulationData,
    ModulationDelta,
    OrgData } from 'types/dataContext';
import { CellGenContractData, CellGenContractDeltaData, CellGenCoworkerDelta, CellGenWeeklyDeltaData, CostCentreHierarchy, CoworkerGridData,
    CoworkerGridDataRef,
    CoworkerGridRow,
    DummyGridData,
    EditableContractHoursCellProps,
    EditableModulationCellProps,
    GenerateEditableCells,
    GridDataCoworkers,
    RowType,
    TimeSelection } from 'types/appContext';
import type { Scenario,
    ScenarioDelta,
    DummyCoworker,
    ContractDelta,
    WeeklyDelta } from 'types/scenario';
import { CoworkerAvailability, Coworker, EmploymentStatus } from 'types/coworker';
import { DateHelper, INDEFINITE_END_DATE, BEGINNING_OF_TIME, isoWeekFromDateString, getOlderAndFutureDates } from 'utils/date';
import { calculateSum, roundValue } from 'utils/number';
import { costCentreIsInList, costCentresAreSame, getShortCC } from 'utils/text';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import type { UserProfile } from 'types/authContext';
import { isIdenticalTimeSelection, isNullOrUndefined } from 'utils/validation';
import { isCoworkersId } from 'utils/rules';
import { getHeaderContribution, getModulationHeaderContribution } from './gridFunctionsHeaders';
import { getAllCostCentresInUnit, rowIsContractHours } from './gridFunctionsShared';
import { getContractCustomContributions,
    getContractDefaultContributions,
    getContributionForDelta,
    getWeeklyDeltaContributions,
    isCustomContributionActive,
    noContribution,
    selectActiveContribution } from './gridFunctionsInputFiles';

const getCostCentreDescription = (
    costCentreCode: string,
    costCentreHierarchy: CostCentreHierarchy | null | undefined
) => costCentreHierarchy?.find(({ costCentre }) => costCentre.costCentreCode === costCentreCode)?.costCentre.costCentreDescription ?? '';

/**
 * extracts the contributions in terms of hours and evenings/weekends that a coworker provides for the given cost centre.
 */
const getContractData = (
    coworker: Coworker | DummyCoworker,
    costCentre: string,
    referenceDate: string,
    defaultContributions: DefaultContributions | undefined,
    customContributions: CustomContributions | undefined,
): CellGenContractData => {
    const isActive = coworker?.status === EmploymentStatus.ACTIVE;
    const contractHours = isActive ? coworker?.contractRange?.range.max ?? coworker?.hoursPerWeek ?? 0 : 0;
    const contractDistribution = (coworker?.costDistributions.find(cd => costCentresAreSame(cd.costCentre, costCentre))?.costCentrePercent ?? 0) / 100;
    const defContributions = getContractDefaultContributions(
        coworker,
        costCentre,
        isActive,
        defaultContributions,
    );

    const custContributions = getContractCustomContributions(
        coworker,
        costCentre,
        referenceDate,
        isActive,
        customContributions,
    );

    return {
        startWeeksFromZero: DateHelper.getDateDifferenceInWeeks(coworker?.contractStartDate || BEGINNING_OF_TIME, referenceDate),
        endWeeksFromZero: DateHelper.getDateDifferenceInWeeks(coworker?.contractEndDate || INDEFINITE_END_DATE, referenceDate),
        weeklyHours: contractHours * contractDistribution,
        timeStamp: BEGINNING_OF_TIME,
        availability: CoworkerAvailability.HOURS,
        startDay: DateHelper.getWeekDay(coworker?.contractStartDate || BEGINNING_OF_TIME),
        endDay: DateHelper.getWeekDay(coworker?.contractEndDate || INDEFINITE_END_DATE),
        defaultContributions: defContributions,
        customContributions: custContributions,
    };
};

/**
 * Calculates the contract hours for a specific week, accounting for partial weeks when a contract starts or ends mid-week.
 * The function handles four checks:
 * 1. Contract both starts AND ends within the same week: Prorates hours based on active days within that week
 *    (e.g., Contract runs Mon-Wed (days 1-3) = weeklyHours * (2+1)/7)
 * 2. Contract starts mid-week but continues beyond it: Prorates hours from the start day to the end of the week
 *    (e.g., Starts on Thursday (day 4) = works 4 days = weeklyHours * (7 - startDay + 1)/7)
 * 3. Contract was already active, ends mid-week: Prorates hours from the beginning of the week to the end day
 *    (e.g., Ends on Wednesday (day 3) = works 3 days = weeklyHours * (3/7))
 * 4. Week is before contract starts OR after contract ends:
 *     Returns 0 hours
 * If none of the conditions above match, the coworker works the full, the full weekly hours are returned.
 * @param contractData - Contract information including weekly hours and start/end dates in weeks from zero
 * @param weeksFromZero - The week number to calculate hours for, relative to the reference date
 * @returns The prorated contract hours for the specified week, rounded to 2 decimal places
 */
const getContractHoursForWeek = (contractData: CellGenContractData, weeksFromZero: number) => {
    let hours = contractData.weeklyHours;
    if (contractData.startWeeksFromZero === weeksFromZero && contractData.endWeeksFromZero === weeksFromZero) {
        hours = contractData.weeklyHours * ((contractData.endDay - contractData.startDay + 1) / 7);
    } else if (contractData.startWeeksFromZero === weeksFromZero && contractData.endWeeksFromZero > weeksFromZero) {
        hours = contractData.weeklyHours * ((7 - contractData.startDay + 1) / 7);
    } else if (contractData.startWeeksFromZero < weeksFromZero && contractData.endWeeksFromZero === weeksFromZero) {
        hours = contractData.weeklyHours * (contractData.endDay / 7);
    } else if (contractData.startWeeksFromZero > weeksFromZero || contractData.endWeeksFromZero < weeksFromZero) {
        hours = 0;
    }

    return Math.round(hours * 100) / 100;
};

const getCoworkerDeltaCellgen = (
    coworkerDelta: Partial<Coworker>,
    coworkerOrDummy: Coworker | DummyCoworker,
    referenceDate:string,
    costCentre: string,
    defaultContributions: DefaultContributions | undefined,
    customContributions: CustomContributions | undefined,
): CellGenCoworkerDelta => {
    const updatedCoworker = { ...coworkerOrDummy, ...coworkerDelta } as Coworker | DummyCoworker;
    const { startWeeksFromZero, endWeeksFromZero, weeklyHours, defaultContributions: defContributions, customContributions: custCont } = getContractData(
        updatedCoworker,
        costCentre,
        referenceDate,
        defaultContributions,
        customContributions,
    );

    return ({
        startWeeksFromZero,
        endWeeksFromZero,
        weeklyHours,
        timeStamp: BEGINNING_OF_TIME,
        availability: CoworkerAvailability.HOURS,
        delta: coworkerDelta,
        defaultContributions: defContributions,
        customContributions: custCont
    });
};

const mapContractDeltasCellgen = (
    contractDeltas: ContractDelta[],
    outputArray: CellGenContractDeltaData,
    costCentre: string,
    coworker: Coworker | DummyCoworker,
    referenceDate: string,
    defaultContributions: DefaultContributions | undefined,
    customContributions: CustomContributions | undefined,
) => {
    contractDeltas.filter(contDelt => contDelt.costDistributions.some(costDist => costCentresAreSame(costDist.costCentre, costCentre)))
        .forEach(contractDelta => {
            const totalHours = contractDelta.hoursPerWeekRange ? contractDelta.hoursPerWeekRange.range.max : contractDelta.hoursPerWeek;
            const distributionInCostcentre = (contractDelta.costDistributions
                .find(cd => costCentresAreSame(cd.costCentre, costCentre))?.costCentrePercent ?? 0) / 100;
            const contributions = getContributionForDelta(
                contractDelta,
                costCentre,
                coworker,
                referenceDate,
                defaultContributions,
                customContributions,
            );
            outputArray.push({
                startWeeksFromZero: DateHelper.getDateDifferenceInWeeks(
                    isoWeekFromDateString(contractDelta.startDate),
                    referenceDate,
                ),
                endWeeksFromZero: DateHelper.getDateDifferenceInWeeks(
                    isoWeekFromDateString(contractDelta.endDate ?? INDEFINITE_END_DATE),
                    referenceDate,
                ),
                weeklyHours: totalHours * distributionInCostcentre,
                timeStamp: contractDelta.timeStamp,
                availability: CoworkerAvailability.HOURS,
                delta: contractDelta,
                defaultContributions: contributions?.defaultContributions ?? noContribution,
                customContributions: contributions?.customContributions ?? null,
            });
        });
};

const mapWeeklyDeltasCellgen = (
    weeklyDeltas: WeeklyDelta[],
    outputArray: CellGenWeeklyDeltaData,
    costCentre: string,
    referenceDate: string,
    coworker: Coworker | DummyCoworker,
    defaultContributions: DefaultContributions | undefined,
    customContributions: CustomContributions | undefined,
) => {
    weeklyDeltas
        .filter(wd => costCentresAreSame(wd.costCentre, costCentre))
        .forEach(weeklyDelta => {
            const hasCustomContribution = customContributions?.some(contribution => (
                isCoworkersId(coworker, contribution.employeeId)
                && getShortCC(contribution.contribution.costCentre) === getShortCC(costCentre)
                && isCustomContributionActive(
                    contribution.contribution.duration.startDate,
                    contribution.contribution.duration.endDate,
                    weeklyDelta.startDate,
                    weeklyDelta.endDate
                )
            )) ?? false;

            outputArray.push({
                startWeeksFromZero: DateHelper.getDateDifferenceInWeeks(
                    isoWeekFromDateString(weeklyDelta.startDate),
                    referenceDate,
                ),
                endWeeksFromZero: DateHelper.getDateDifferenceInWeeks(
                    isoWeekFromDateString(weeklyDelta.endDate),
                    referenceDate,
                ),
                weeklyHours: weeklyDelta.costCentreHours,
                timeStamp: weeklyDelta.timeStamp,
                availability: weeklyDelta.coworkerAvailability,
                delta: weeklyDelta,
                defaultContributions: getWeeklyDeltaContributions(
                    weeklyDelta,
                    costCentre,
                    defaultContributions,
                    hasCustomContribution, // If there is a custom contribution, it will be handled in the contract deltas unless weekly delta sets hours to 0
                ),
                customContributions: null, // If there is a custom contribution, it will be handled in the contract deltas
            });
        });
};

const generateContractHoursEditableCellsProps = ({
    coworker,
    delta,
    timeArray,
    costCentre,
    defaultContributions,
    customContributions,
}: GenerateEditableCells) => {
    const coworkerOrDummy = coworker ?? delta?.dummyCoworker;
    if (timeArray.length === 0 || !coworkerOrDummy) return [];

    const editableCellRow: Array<EditableContractHoursCellProps> = [];

    const contractData: CellGenContractData = getContractData(coworkerOrDummy, costCentre, timeArray[0], defaultContributions, customContributions);
    timeArray.forEach((week, index) => {
        const hours = getContractHoursForWeek(contractData, index);
        const contributions = selectActiveContribution(index, contractData.defaultContributions, contractData.customContributions);
        editableCellRow.push({
            elementId: `${coworkerOrDummy.personId}:${costCentre}:${week}`,
            originalValue: coworker ? hours : 0, // Always 0 for dummy coworkers
            currentValue: hours,
            key: `${coworkerOrDummy.personId}:${costCentre}:${week}-cell`,
            coworkerAvailability: CoworkerAvailability.HOURS,
            contributions: contributions ?? noContribution,
        });
    });

    if (delta) {
        const contractDeltas: CellGenContractDeltaData = [];
        const weeklyDeltas: CellGenWeeklyDeltaData = [];
        let coworkerDelta: CellGenCoworkerDelta;
        if (delta.coworkerDelta) {
            coworkerDelta = getCoworkerDeltaCellgen(delta.coworkerDelta, coworkerOrDummy, timeArray[0], costCentre, defaultContributions, customContributions);
        }
        if (delta.contractDeltas?.length) {
            mapContractDeltasCellgen(
                delta.contractDeltas,
                contractDeltas,
                costCentre,
                coworkerOrDummy,
                timeArray[0],
                defaultContributions,
                customContributions,
            );
        }
        if (delta.weeklyDeltas?.length) {
            mapWeeklyDeltasCellgen(
                delta.weeklyDeltas,
                weeklyDeltas,
                costCentre,
                timeArray[0],
                coworkerOrDummy,
                defaultContributions,
                customContributions,
            );
        }
        const sortedWeeklyDeltas = weeklyDeltas.sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime());
        const sortedContractDeltas = contractDeltas.sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime());

        timeArray.forEach((_week, index) => {
            // Note: Changes are always weekly.
            const latestWeeklyDelta = sortedWeeklyDeltas.find(
                sortedDelta => sortedDelta.startWeeksFromZero <= index && index <= sortedDelta.endWeeksFromZero,
            );
            const latestContractDelta = sortedContractDeltas.find(
                sortedDelta => sortedDelta.startWeeksFromZero <= index && index <= sortedDelta.endWeeksFromZero,
            );
            const latestCoworkerDelta = coworkerDelta && coworkerDelta.startWeeksFromZero <= index && index <= coworkerDelta.endWeeksFromZero
                ? coworkerDelta
                : undefined;

            const allDeltas = [latestContractDelta, latestWeeklyDelta, latestCoworkerDelta].filter(Boolean);

            const sortedDeltas = allDeltas.length
                ? allDeltas.sort(
                    (a, b) => new Date(b?.timeStamp ?? BEGINNING_OF_TIME).getTime()
                          - new Date(a?.timeStamp ?? BEGINNING_OF_TIME).getTime(),
                )
                : null;
            const activeDelta = sortedDeltas?.[0];
            const activeContribution = sortedDeltas?.map(
                del => selectActiveContribution(index, del?.defaultContributions ?? null, del?.customContributions ?? null),
            )?.find(contribution => !isNullOrUndefined(contribution)) ?? null;
            if (activeDelta) {
                editableCellRow[index].currentValue = activeDelta.weeklyHours;
                editableCellRow[index].coworkerAvailability = activeDelta.availability;
                editableCellRow[index].scenarioDeltas = {
                    latestContractDelta: latestContractDelta?.delta,
                    latestWeeklyDelta: latestWeeklyDelta?.delta,
                    latestCoworkerDelta: coworkerDelta?.delta,
                };
            }
            if (activeContribution) {
                editableCellRow[index].contributions = activeContribution;
            }
        });
    }

    return editableCellRow;
};

const getCoworkerModulation = (
    coworker: Coworker | undefined,
    timeArray: Array<string>,
    modulationData: ModulationDelta[] | undefined,
) => {
    const row: Array<EditableModulationCellProps> = timeArray.map((week: string) => {
        const currentValue = modulationData
            ?.find(m => m.employeeId === coworker?.personId && isoWeekFromDateString(m.modulationDate) === week)?.modulationDelta ?? 0;

        return {
            elementId: `modulation-${coworker?.personId}:${week}`,
            originalHours: currentValue,
            originalMinutes: currentValue,
            currentHours: currentValue,
            currentMinutes: currentValue,
            key: `modulation-${coworker?.personId}:${week}`,
        };
    });

    return row;
};

export const generateTotalHoursCellProps = (
    costCentre: string,
    personId: string,
    gridRows: Array<CoworkerGridRow>,
    timeArray: Array<string>,
) => {
    const row: Array<EditableContractHoursCellProps> = [];

    timeArray.forEach((time, index) => {
        const elementId = `total-hours-${personId}:${costCentre}:${time}`;
        const currentValue = calculateSum(gridRows.map(el => {
            if (rowIsContractHours(el)) {
                return Number(el.editableCells[index].currentValue);
            }

            if (el.rowType === RowType.MODULATION) {
                const castedModulationEditableCell = (el.editableCells as Array<EditableModulationCellProps>)[index];
                //* Important: removed the decimal conversion because we dont have hours and minutes in the modulation data
                // return getHoursInDecimal(castedModulationEditableCell.currentHours, castedModulationEditableCell.currentMinutes);

                return (castedModulationEditableCell.currentHours);
            }

            return 0;
        }));

        row.push({
            elementId,
            originalValue: currentValue,
            currentValue,
            key: elementId,
            coworkerAvailability: CoworkerAvailability.HOURS,
            contributions: noContribution,
        });
    });

    return row;
};

const getContractDelta = (personId: string, scenario: Scenario | undefined): ScenarioDelta | null => {
    if (!scenario) {
        return null;
    }

    const coworkerScenarioRaw = scenario.scenarioData.find(delta => delta.personId === personId);

    return coworkerScenarioRaw ?? null;
};

const getCoworkerCostCenters = (coworker: Coworker | DummyCoworker | undefined, cwScenarioDelta: ScenarioDelta | null) => {
    const ccList = coworker?.costDistributions.map(cd => cd.costCentre) || [];

    cwScenarioDelta?.dummyCoworker?.costDistributions.forEach(cd => {
        if (!ccList.includes(cd.costCentre) && cd.costCentre) {
            ccList.push(cd.costCentre);
        }
    });

    cwScenarioDelta?.contractDeltas.forEach(delta => {
        delta?.costDistributions?.forEach(cd => {
            if (!ccList.includes(cd.costCentre) && cd.costCentre) {
                ccList.push(cd.costCentre);
            }
        });
    });

    return ccList;
};

/**
 * Checks if a coworker is active during the selected time period.
 * @returns `true` if the coworker is active during the time selection; otherwise, `false`.
 */
const isCoworkerActiveInTimeSelection = (
    coworker: Coworker,
    timeSelection: TimeSelection,
) => {
    const selectionStartDate = timeSelection.timeArray[0];
    const selectionEndDate = timeSelection.timeArray[timeSelection.timeArray.length - 1];

    const validityStartDate = coworker.validity[0];
    const validityEndDate = coworker.validity[1];

    const selectionStart = DateHelper.getISOWeekStartDate(selectionStartDate);
    const selectionEnd = DateHelper.getISOWeekEndDate(selectionEndDate);
    const validityStart = moment(validityStartDate, 'YYYY-MM-DD');
    const validityEnd = moment(validityEndDate, 'YYYY-MM-DD');

    // validity starts before/on selection ends AND validity ends after/on selection starts
    return validityStart.isSameOrBefore(selectionEnd) && validityEnd.isSameOrAfter(selectionStart);
};

const getFilteredCoworkerForGrid = (
    coworkers: Array<Coworker> | undefined,
    timeSelection: TimeSelection,
) => {
    if (!coworkers) {
        return [];
    }

    return coworkers.filter(coworker => isCoworkerActiveInTimeSelection(coworker, timeSelection));
};

const getContractsInChronologicalOrder = (firstContract: Coworker, secondContract: Coworker): [Coworker, Coworker] => {
    const firstValidity = getOlderAndFutureDates(firstContract.validity).startDate;
    const secondValidity = getOlderAndFutureDates(secondContract.validity).startDate;

    return firstValidity && secondValidity && firstValidity.localeCompare(secondValidity) <= 0
        ? [firstContract, secondContract]
        : [secondContract, firstContract];
};

/**
 * This function compares all attributes of the Coworker object and identifies any differences
 * @param firstContract - the first contract to compare, used as baseline for comparison
 * @param secondContract - the second contract to compare against the previous contract
 * @returns a futureData object containing the differences in contract attributes between the two contracts
 */
const getContractDifferences = (firstContract: Coworker, secondContract: Coworker): Partial<Coworker> => {
    const changesInNewContract: Record<string, Coworker[keyof Coworker]> = {};
    const [existingCoworker, incomingCoworker] = getContractsInChronologicalOrder(firstContract, secondContract);
    const excludedDiffKeys = new Set<keyof Coworker>([
        'futureData',
        'contributions',
        'createdBy',
        'createdOn',
        'isContractRangeSupport',
        'contractRange',
        'incomplete',
        'emailType',
        'networkId',
        'managerPersonId',
        'flags',
        'validity',
        'orgUnitId',
        'costDistributions'
    ]);

    (Object.keys(existingCoworker)).forEach(eachKey => {
        if (excludedDiffKeys.has(eachKey as keyof Coworker)) {
            return;
        }
        const existingValue = existingCoworker[eachKey as keyof Coworker];
        const incomingValue = incomingCoworker[eachKey as keyof Coworker];

        if (!isEqual(existingValue, incomingValue)) {
            changesInNewContract[eachKey] = incomingValue;
        }
    });

    return changesInNewContract;
};

/**
 *
 * @param coworker Single Object of a coworker
 * @param gridRows Array of grid rows per coworker, can contain multiple rows for the same cost centre if coworker has multiple contracts with same cost centre
 * @param timeSelection Object containing startDate and endDate of the time selection period
 * @param currentUnitRates Object containing the current unit rates for different cost centres
 * @returns Array of grid row for the coworker with contract hours rows merged by cost centre
 */
const mergeContractRowsByCostCentre = (
    coworker: Coworker | DummyCoworker,
    gridRows: Array<CoworkerGridRow>,
    timeSelection: TimeSelection,
    currentUnitRates: InputRates | undefined,
): Array<CoworkerGridRow> => {
    /**
     * Group gridRows (multiple contracts) by same costCentre.
     * MODULATION rows are kept as-is beause we only focus on merging contract hours rows.
     */
    const multipleContractsByCostCentre = new Map<string, Array<CoworkerGridRow>>();
    gridRows.forEach(row => {
        const rows = multipleContractsByCostCentre.get(row.costCentre) ?? [];
        rows.push(row);
        multipleContractsByCostCentre.set(row.costCentre, rows);
    });

    const mergedRows: Array<CoworkerGridRow> = [];
    multipleContractsByCostCentre.forEach((rows, costCentre) => {
        const contractRows = rows.filter(row => row.rowType === RowType.CONTRACT_HOURS);
        const otherRows = rows.filter(row => row.rowType !== RowType.CONTRACT_HOURS);

        if (contractRows.length > 1) {
            const editableCellsMap = new Map<string, EditableContractHoursCellProps>();
            contractRows.forEach(row => {
                row.editableCells.forEach(cell => {
                    const existing = editableCellsMap.get(cell.key);
                    if (existing) {
                        existing.originalValue += cell.originalValue;
                        /**
                         * When merging multiple contracts for the same cost centre
                         * A scenario delta is TOTAL hours user has changed for a week (not contract data).
                         * Multiple contracts can have same delta in editableProps, we apply only once to avoid duplication.
                         * Three merge scenarios:
                         * 1. Existing cell has delta → keep it, ignore incoming value (prevents double counting)
                         * 2. Incoming cell has delta but existing doesn't → use the delta value instead of summing
                         * 3. Neither has delta → both are base contract hours, so safely sum them
                         */
                        if (!existing.scenarioDeltas && cell.scenarioDeltas) {
                            existing.currentValue = cell.currentValue;
                            existing.scenarioDeltas = cell.scenarioDeltas;
                        } else if (!existing.scenarioDeltas && !cell.scenarioDeltas) {
                            existing.currentValue += cell.currentValue;
                        }
                        // existing.scenarioDeltas already set → delta already captured, skip incoming currentValue
                    } else {
                        editableCellsMap.set(cell.key, { ...cell });
                    }
                });
            });

            const mergedEditableCells = Array.from(editableCellsMap.values());
            /** Recalculate header contributions from the merged cells */
            const headerContributions = mergedEditableCells.map(
                (editableCell, index) => getHeaderContribution(
                    coworker,
                    editableCell,
                    costCentre,
                    timeSelection.timeArray[index],
                    currentUnitRates,
                )
            );

            const rowSum = calculateSum(mergedEditableCells.map(p => (typeof p.currentValue === 'number' ? p.currentValue : 0)));
            const rowAverage = rowSum / mergedEditableCells.length || 0;

            mergedRows.push({
                costCentre,
                editableCells: mergedEditableCells,
                rowSum: roundValue(rowSum),
                rowAverage: roundValue(rowAverage),
                rowType: RowType.CONTRACT_HOURS,
                headerContributions,
            });
        } else {
            mergedRows.push(...contractRows);
        }

        mergedRows.push(...otherRows);
    });

    return mergedRows;
};

const mergeCwMultipleContracts = (
    gridData: Array<CoworkerGridData>,
    timeSelection: TimeSelection,
    currentUnitRates: InputRates | undefined,
) => {
    const coworkerMap = new Map<string, CoworkerGridData>();
    // Group by personId:costCentre entries for the same coworker, same cc are grouped into one array(handles multiple contract periods)
    gridData.forEach(currentRow => {
        const key = `${currentRow.coworker.personId}:${currentRow.coworker.costCentre}`;
        const existing = coworkerMap.get(key);
        if (!existing) {
            coworkerMap.set(key, currentRow);

            return;
        }
        const [currentCoworker, futureCoworker] = getContractsInChronologicalOrder(existing.coworker, currentRow.coworker);
        const futureContractData = getContractDifferences(currentCoworker, futureCoworker);
        const mergedValidityPeriods = [...existing.coworker.validity, ...currentRow.coworker.validity];
        const accumulatedFutureData: Array<Partial<Coworker>> = [...(currentCoworker.futureData ?? [])];
        if (Object.values(futureContractData).some(eachValue => !isNullOrUndefined(eachValue))) {
            accumulatedFutureData.push(futureContractData);
        }
        const mergedCoworker: Coworker = { ...currentCoworker, validity: mergedValidityPeriods, futureData: accumulatedFutureData };
        coworkerMap.set(key, { coworker: mergedCoworker, gridRows: [...existing.gridRows, ...currentRow.gridRows] });
    });
    const coworkerGridDataArray = Array.from(coworkerMap.values());

    return coworkerGridDataArray.map(eachContractRow => {
        const { startDate: oldestDate, endDate: futureDate } = getOlderAndFutureDates(eachContractRow.coworker.validity);

        /**
         *! Here its very important that we take the futureDate for greyedOutCell in Grid
         * i.e. contract1: Jan 1st - Jun6th, contract2: Jun 7th - Dec 31st
         * we take the startDate as Jan 1st and endDate as Dec 31st for grid display but doesnt have any impact on calculations
         * we already have editableCell values generated only for valid contracts
         */
        const updatedCoworker: Coworker = { ...eachContractRow.coworker, contractStartDate: oldestDate, contractEndDate: futureDate };

        return {
            coworker: updatedCoworker,
            gridRows: mergeContractRowsByCostCentre(updatedCoworker, eachContractRow.gridRows, timeSelection, currentUnitRates),
        };
    });
};

/**
 * Function that generates the data used in the manage capacity row tables
 * for all the coworkers in the grid and any dummy coworkers in the scenario.
 * @param coworkers Array of coworkers
 * @param scenario Current scenario
 * @param timeSelection TimeSelection
 * @param costCentreList List of cost centres
 * @param hasModulationAccess If the user has access to modulation
 * @param currentUnitRates Unit rates/ Capacity factors
 * @param defaultContributions Contributions from the default contributions
 * @param customContributions Any custom contributions
 * @param modulationData Modulation data
 * @returns Array<CoworkerGridData | DummyGridData>
 */
const generateGridRows = (
    {
        coworkers,
        scenario,
        timeSelection,
        costCentreList,
        hasModulationAccess,
        currentUnitRates,
        defaultContributions,
        customContributions,
        modulationData,
    } : {
        coworkers: Array<Coworker> | undefined,
        scenario: Scenario | undefined,
        timeSelection: TimeSelection,
        costCentreList: Array<string>,
        hasModulationAccess: boolean,
        currentUnitRates: InputRates | undefined,
        defaultContributions: DefaultContributions | undefined,
        customContributions: CustomContributions | undefined,
        modulationData: ModulationData | undefined,
    }
) => {
    const scenarioCwsData: Array<DummyGridData> = [];
    const paDataCoworkers: Array<CoworkerGridData> = [];

    // When coworker IS dummy coworker
    scenario?.scenarioData.filter(scenDelta => scenDelta.dummyCoworker)
        .forEach(scenDelta => {
            const confirmedDummyCoworker = scenDelta.dummyCoworker!;
            const costCenters = getCoworkerCostCenters(confirmedDummyCoworker, scenDelta);

            const coworkerGridRows: Array<CoworkerGridRow> = costCenters.map(ccId => {
                const editableCells = generateContractHoursEditableCellsProps({
                    coworker: undefined, // Always undefined for dummy coworkers, contract hours are derived from scenario deltas
                    delta: scenDelta,
                    timeArray: timeSelection.timeArray,
                    costCentre: ccId,
                    defaultContributions,
                    customContributions,
                });
                const rowSum = calculateSum(editableCells.map(p => (typeof p.currentValue === 'number' ? p.currentValue : 0)));

                return {
                    costCentre: ccId,
                    editableCells,
                    rowSum: costCentreList.includes(ccId) ? roundValue(rowSum) : 0,
                    rowAverage: roundValue(rowSum / editableCells.length) || 0,
                    rowType: RowType.CONTRACT_HOURS,
                    headerContributions: editableCells.map((editableCell, index) => getHeaderContribution(
                        confirmedDummyCoworker,
                        editableCell,
                        ccId,
                        timeSelection.timeArray[index],
                        currentUnitRates,
                    )),
                };
            });

            scenarioCwsData.push({
                coworker: confirmedDummyCoworker,
                gridRows: coworkerGridRows,
            });
        });

    const filteredCoworkers = getFilteredCoworkerForGrid(coworkers, timeSelection);
    // coworker IS NOT dummy, actual PA coworker data for business unit
    filteredCoworkers?.forEach(coworker => {
        const cwScenarioDelta = getContractDelta(coworker.personId, scenario);

        const costCentres = getCoworkerCostCenters(coworker, cwScenarioDelta);
        const gridRows: Array<CoworkerGridRow> = [];
        const hasModulation = !isNullOrUndefined(modulationData?.[coworker.personId]); // Check if the coworker has modulation data
        costCentres.forEach(ccId => {
            const editableCells = generateContractHoursEditableCellsProps({
                coworker,
                delta: cwScenarioDelta,
                timeArray: timeSelection.timeArray,
                costCentre: ccId,
                defaultContributions,
                customContributions
            });
            const rowSum = calculateSum(editableCells.map(p => (typeof p.currentValue === 'number' ? p.currentValue : 0)));
            const rowAverage = roundValue(rowSum / editableCells.length) || 0;

            const headerContributions = editableCells.map(
                (editableCell, index) => getHeaderContribution(
                    coworker,
                    editableCell,
                    ccId,
                    timeSelection.timeArray[index],
                    currentUnitRates,
                )
            );

            gridRows.push({
                costCentre: ccId,
                editableCells,
                rowSum: costCentreList.some(ccItem => ccItem.includes(ccId)) ? roundValue(rowSum) : 0,
                rowAverage,
                rowType: RowType.CONTRACT_HOURS,
                headerContributions,
            });

            // Add modulation for the current coworker
            if (hasModulationAccess && hasModulation) {
                const modulationEditableCells = getCoworkerModulation(
                    coworker,
                    timeSelection.timeArray,
                    modulationData?.[coworker.personId],
                );

                const modulationRowSum = calculateSum(modulationEditableCells.map(value => value.originalHours));
                const modulationRowAverage = roundValue(modulationRowSum / editableCells.length) || 0;

                const modulationHeaderContributions = modulationEditableCells.map(
                    (editableCell, index) => getModulationHeaderContribution(
                        editableCell,
                        ccId,
                        timeSelection.timeArray[index],
                        currentUnitRates,
                    )
                );
                gridRows.push({
                    costCentre: ccId,
                    editableCells: modulationEditableCells,
                    rowSum: costCentreList.includes(ccId) ? roundValue(modulationRowSum) : 0,
                    rowAverage: modulationRowAverage,
                    rowType: RowType.MODULATION,
                    headerContributions: modulationHeaderContributions,
                });
            }
        });

        /** Using timeRangeCoworkers as temporary storage to hold coworker data with updated contract dates before merging grid rows with same cost centre */
        paDataCoworkers.push({
            // TODO: Add this isModulated in PA Data then remove here
            // coworker: hasModulation ? { ...coworker, isModulated: true } : coworker,
            coworker,
            gridRows,
        });
    });

    const paCoworkerData = mergeCwMultipleContracts(
        paDataCoworkers.sort((a, b) => a.coworker.personId.localeCompare(b.coworker.personId)),
        timeSelection,
        currentUnitRates
    );
    const gridData: Array<CoworkerGridData | DummyGridData> = Array.from([...scenarioCwsData, ...paCoworkerData]);

    return gridData;
};

/**
 * Filters the gridRows by removing any coworkers that do not belong to any cost centre
 * in the costCentreList.
 * @param gridRowsData gridRows generated by generateGridRows
 * @param costCentreList List of cost centres
 * @returns \{coworkers: Array<(DummyGridData | CoworkerGridData)>}
 */
const filterGridDataByCostCentre = (gridRowsData: GridDataCoworkers | undefined, costCentreList: string[] | undefined) => {
    if (!gridRowsData) {
        return undefined;
    }

    return {
        coworkers: gridRowsData.coworkers
            .filter(coworker => !costCentreList || costCentreList.length === 0 || coworker.gridRows
                .some(row => costCentreIsInList(row.costCentre, costCentreList))) ?? [],
    };
};

/**
 * A function which merges two gridRows objects. The scenarioData object always takes priority over
 * the coworkerData object. So if a coworker is present in both objects, the coworker in scenarioData
 * overwrites the coworker in coworkerData when producing the final output.
 * @param coworkerData gridRows generated by generateGridRows without the scenario data. Contains all the coworkers in the unit
 * @param scenarioData gridRows generated by generateGridRows with only coworkers/dummys affected by the scenario data
 * @returns A merged object where coworkers in scenarioData replace corresponding coworkers in coworkerData
 */
const mergeRowsData = (
    coworkerData: GridDataCoworkers | undefined,
    scenarioData: GridDataCoworkers | undefined
): GridDataCoworkers | undefined => {
    if (!coworkerData && !scenarioData) {
        return undefined;
    }
    if (!coworkerData?.coworkers) {
        return cloneDeep(scenarioData);
    }
    if (!scenarioData?.coworkers) {
        return { coworkers: cloneDeep(coworkerData.coworkers) };
    }

    const mergedList = cloneDeep(coworkerData.coworkers);
    const clonedScenarioData = cloneDeep(scenarioData);
    clonedScenarioData.coworkers.forEach(scenarioCoworker => {
        if (scenarioCoworker.coworker.isDummy) {
            mergedList.unshift(scenarioCoworker);
        } else {
            mergedList[mergedList.findIndex(coworker => coworker.coworker.personId === scenarioCoworker.coworker.personId)] = scenarioCoworker;
        }
    });

    return {
        coworkers: mergedList
    };
};

/**
 * A helper function which generates gridrows for all coworkers/dummy coworkers which are in the scenario.
 * and have a cost centre in the costCentreList.
 * @param param0
 * @returns gridRows for all coworkers/dummy coworkers which are in the scenario.
 */
const getScenarioGridRows = (
    {
        costCentreList,
        orgData,
        currentUnit,
        currentUnitType,
        coworkers,
        currentScenario,
        timeSelection,
        hasModulationAccess,
        currentUnitRates,
        defaultContributions,
        customContributions,
        modulationData,
    } : {
        costCentreList: Array<string> | undefined,
        orgData: OrgData | undefined,
        currentUnit: string,
        currentUnitType: string,
        coworkers: Array<Coworker> | undefined,
        currentScenario: Scenario | undefined,
        timeSelection: TimeSelection,
        hasModulationAccess: boolean,
        currentUnitRates: InputRates | undefined,
        defaultContributions: DefaultContributions | undefined,
        customContributions: CustomContributions | undefined,
        modulationData: ModulationData | undefined,
    }
) => {
    const costCentres = costCentreList ?? getAllCostCentresInUnit(orgData, currentUnit, currentUnitType);
    const coworkerIdsInScenario = currentScenario?.scenarioData
        .filter(data => data.coworkerSource === 'PA')
        .map(data => data.personId) ?? [];
    const coworkersInScenario = coworkers?.filter(coworker => coworkerIdsInScenario.includes(coworker.personId)) ?? [];

    return {
        coworkers: generateGridRows(
            {
                coworkers: coworkersInScenario,
                scenario: currentScenario,
                timeSelection,
                costCentreList: costCentres,
                hasModulationAccess,
                currentUnitRates,
                defaultContributions,
                customContributions,
                modulationData
            }
        )
    };
};

/**
 * A helper function which primarily keeps track of whether or not the
 * gridDataRef needs to be updated. If it does, it uses generateGridRows
 * on all the coworkers in all the cost centres and stores the data for future use.
 * @param param0
 * @returns CoworkerGridDataRef (which includes gridrows and trackers
 * for whether or not the data needs to be updated)
 */
const updateCoworkerGridDataRef = (
    {
        coworkerGrid,
        coworkers,
        timeSelection,
        user,
        currentUnit,
        currentUnitType,
        orgData,
        currentUnitRates,
        defaultContributions,
        customContributions,
        hasModulationAccess,
        modulationData,
    }:
    {
        coworkerGrid: CoworkerGridDataRef | undefined,
        coworkers: Array<Coworker> | undefined,
        timeSelection: TimeSelection,
        user: UserProfile | null,
        currentUnit: string | undefined,
        currentUnitType: string | undefined,
        orgData: OrgData | undefined,
        currentUnitRates: InputRates | undefined,
        defaultContributions: DefaultContributions | undefined,
        customContributions: CustomContributions | undefined,
        hasModulationAccess: boolean,
        modulationData: ModulationData | undefined,
    }
) => {
    if (
        coworkerGrid
                && coworkerGrid.coworkers.length > 0
                && coworkerGrid.coworkers.length === coworkers?.length
                && isIdenticalTimeSelection(coworkerGrid.timeSelection, timeSelection)
                && currentUnit === coworkerGrid.currentUnit
                && orgData?.country === coworkerGrid.currentCountry
                && (coworkerGrid?.defaultContributionsIdentifier
                    && defaultContributions && JSON.stringify(defaultContributions) === coworkerGrid.defaultContributionsIdentifier)
                && (coworkerGrid?.customContributionsIdentifier
                    && customContributions && JSON.stringify(customContributions) === coworkerGrid.customContributionsIdentifier)
                && (coworkerGrid?.rateIdentifier
                    && currentUnitRates && JSON.stringify(currentUnitRates) === coworkerGrid.rateIdentifier)
    ) {
        /* This prevents recalculations of the coworkerGridDataRef if nothing has changed.
        If we get here, it means that the coworkerGridDataRef is already up to date. */

        return coworkerGrid;
    }
    if (!user
                || !timeSelection
                || !timeSelection.valid
                || !currentUnit
                || !currentUnitType
                || !coworkers) {
        /* These are the required values to calculate the gridData.
            If any of them are missing, we return undefined */

        return undefined;
    }

    return {
        /*
            TODO: Figure out a way to get more efficient identifiers && other ways to reduce redundant calculations when moving timeselection
            Now it calculates once when timeselection updates, then again when rates api call is resolved, and this is the heaviest calculation.
            We want to suspend calculation pending the rates api call, and make sure we dont recalculate if nothing has changed.
            Add a recieved at timestamp in the front end and use as an identifier to replace stringify()?
            Note that different timeselections could in theory yield identical rates (if they span beyond end of last year), so making a validating function
            to prevent unneeded calculations can be difficult (i.e. if (!validateRatesUsingTimeSelection(timeSelection, currentUnitRates)) return undefined)).
        */
        rateIdentifier: currentUnitRates ? JSON.stringify(currentUnitRates) : 'no data',
        defaultContributionsIdentifier: defaultContributions ? JSON.stringify(defaultContributions) : 'no data',
        customContributionsIdentifier: customContributions ? JSON.stringify(customContributions) : 'no data',
        timeSelection,
        currentUnit,
        currentCountry: `${orgData?.country}`,
        coworkers: generateGridRows(
            {
                coworkers,
                scenario: undefined,
                timeSelection,
                costCentreList: getAllCostCentresInUnit(orgData, currentUnit, currentUnitType),
                hasModulationAccess,
                currentUnitRates,
                defaultContributions,
                customContributions,
                modulationData,
            }
        ),
    };
};

export {
    mergeRowsData,
    getScenarioGridRows,
    updateCoworkerGridDataRef,
    generateContractHoursEditableCellsProps,
    generateGridRows,
    getContractDelta,
    filterGridDataByCostCentre,
    getContractDifferences,
    getCostCentreDescription,
    getFilteredCoworkerForGrid
};
